/**
 * Copyright 2016 xdot GmbH
 *
 * Author: Carsten Hibbeler
 * Date: 20.06.2016
 *
 */
import React from 'external/react';
import PropTypes from 'external/prop-types';
import { AppContext } from '../app-context';
import { getFormStore } from '../stores/StoreFactory';
import FormActions from '../actions/FormActions';
import FormValidationUtil from '../utils/form-validation-util';

const FormStore = getFormStore(AppContext);

let formIdCounter = 0;

function getDisplayName(WrappedComponent){
    return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export default function wrapWithFormContainer(FormComponent){
    class FormContainer extends React.Component {

        constructor(props){
            super(props);
            this.state = this.calculateState({
                formId: props.formId || ++formIdCounter,
                editRequired: props.editRequired !== false
            }, props);
        }

        componentDidMount(){
            this.removeListener = FormStore.addListener(this.onChange.bind(this));
        }

        componentWillUnmount(){
            if (FormStore.isEditing(this.state.formId)) {
                console.warn('Form component unmounted but form is already editing');
            }
            if (this.removeListener) {
                this.removeListener.remove();
            }
        }

        onChange(){
            this.setState(this.calculateState(this.state, this.props));
        }

        startEdit(){
            FormActions.startEditFormItem(this.state.formId, this.props.item);
        }

        calculateState(prevState, prevProps){
            const formId = prevState && prevState.formId || prevProps && prevProps.formId;
            const formValidation = FormStore.getValidation(formId);
            const formValid = this.isFormValid(formValidation);
            return {
                formId: formId,
                formData: FormStore.getItem(formId),
                originalFormData: FormStore.getOriginalItem(formId),
                formValidation: formValidation,
                formError: FormStore.getError(formId),
                isEditing: FormStore.isEditing(formId),
                isSaving: FormStore.isSaving(formId),
                isEdited: FormStore.isEdited(formId),
                formValid: formValid
            };
        }

        isFormValid(formValidation){
            return FormValidationUtil.isFormValid(formValidation);
        }

        handleFormChange(data, validation){
            FormActions.changedEditFormItem(this.state.formId, data, validation);
        }

        submitForm(){
            this.props.onFormSubmit(this.state.formData, this.state.formValidation, this.state.formId);
        }

        cancelForm(){
            FormActions.stopEditFormItem(this.state.formId);
        }

        resetForm(){
            FormActions.resetForm(this.state.formId);
        }

        render(){
            return (
                <FormComponent
                    {...this.props}
                    onChange={this.handleFormChange.bind(this)}
                    cancel={this.cancelForm.bind(this)}
                    submit={this.submitForm.bind(this)}
                    reset={this.resetForm.bind(this)}
                    startEdit={this.startEdit.bind(this)}
                    formId={this.state.formId}
                    formData={this.state.formData}
                    formValid={this.state.formValid}
                    isEdited={this.state.editRequired ? this.state.isEdited : true}
                    isEditing={this.state.isEditing}
                    isSaving={this.state.isSaving}
                    formError={this.state.formError}
                    originalFormData={this.state.originalFormData}
                    formValidation={this.state.formValidation}
                />
            );
        }
    }

    FormContainer.propTypes = {
        formId: PropTypes.string.isRequired,
        onFormSubmit: PropTypes.func.isRequired,
        editRequired: PropTypes.bool,
        item: PropTypes.object
    };

    FormContainer.displayName = `Connect(${getDisplayName(FormComponent)})`;

    return FormContainer;
}




// WEBPACK FOOTER //
// ./js/components/FormContainer.jsx